import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useAnimatedNumber } from '../../hooks/useAnimatedNumber';
import { useLevels } from '../../hooks/useLevels';
import { iconFor, getLevelForScore } from '../../utils/levels';
import { Trophy } from 'lucide-react';
import { avatarSrc } from '../avatar/avatarList';
import RanksDialog from '../ui/RanksDialog';
import './Leaderboard.css';

const LevelPill = ({ level }) => {
  if (!level) return null;
  const Icon = iconFor(level.icon);
  return (
    <span
      className="lb-level-pill"
      style={{ color: level.color, borderColor: `${level.color}55`, background: `${level.color}14` }}
      title={level.name}
    >
      <Icon size={11} strokeWidth={2.4} />
      {level.name}
    </span>
  );
};

LevelPill.propTypes = { level: PropTypes.object };

const AnimatedScore = ({ value, delay = 0, duration = 1400 }) => {
  const animated = useAnimatedNumber(value, { duration, delay });
  return <>{animated.toLocaleString()}</>;
};

AnimatedScore.propTypes = {
  value: PropTypes.number.isRequired,
  delay: PropTypes.number,
  duration: PropTypes.number,
};

const PodiumSlot = ({ player, rank, isCurrentUser, level }) => {
  if (!player) return <div className={`podium-slot podium-slot--${rank} podium-slot--empty`} />;

  // Stagger so #1 settles last for a satisfying reveal.
  const scoreDelay = rank === 1 ? 500 : rank === 2 ? 300 : 200;
  const scoreDuration = rank === 1 ? 1800 : 1400;

  return (
    <div className={`podium-slot podium-slot--${rank}${isCurrentUser ? ' podium-slot--you' : ''}`}>
      <div className="podium-above">
        {rank === 1 && (
          <div className="podium-crown"><Trophy size={18} /></div>
        )}
        <div className={`podium-avatar podium-avatar--${rank}`}>
          {player.avatar
            ? <img src={avatarSrc(player.avatar)} alt="" />
            : player.name?.charAt(0).toUpperCase()}
        </div>
        <div className="podium-info">
          <span className="podium-name">
            {player.name?.split(' ')[0]}
            {isCurrentUser && <span className="you-tag">You</span>}
          </span>
          <span className="podium-score">
            <AnimatedScore value={player.best_score || 0} delay={scoreDelay} duration={scoreDuration} />
          </span>
          <span className="podium-meta">{player.games_played} games</span>
          <LevelPill level={level} />
        </div>
      </div>
      <div className={`podium-platform podium-platform--${rank}`}>
        <span className="podium-rank-num">{rank}</span>
      </div>
    </div>
  );
};

PodiumSlot.propTypes = {
  player: PropTypes.object,
  rank: PropTypes.number.isRequired,
  isCurrentUser: PropTypes.bool,
  level: PropTypes.object,
};

const SkeletonRow = () => (
  <div className="lb-row lb-skeleton">
    <div className="skel" style={{ width: 28, height: 14, flexShrink: 0 }} />
    <div className="skel" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="skel" style={{ width: 140, height: 14 }} />
      <div className="skel" style={{ width: 80, height: 11 }} />
    </div>
    <div className="skel" style={{ width: 56, height: 16 }} />
  </div>
);

const Leaderboard = () => {
  const { request, loading } = useApi();
  const { user: currentUser } = useAuth();
  const { levels } = useLevels();
  const [leaderboard, setLeaderboard] = useState([]);
  const [ranksOpen, setRanksOpen] = useState(false);
  const [dialogScore, setDialogScore] = useState(0);

  const openRanks = (score) => {
    setDialogScore(score || 0);
    setRanksOpen(true);
  };

  useEffect(() => {
    request('get', '/leaderboard').then(data => {
      setLeaderboard(data.leaderboard || []);
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ranked = leaderboard.filter(p => (p.games_played || 0) > 0 || (p.best_score || 0) > 0);
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  const userInList = currentUser && ranked.some(p => p.id === currentUser.id);
  const currentUserEntry = currentUser && !userInList
    ? leaderboard.find(p => p.id === currentUser.id) ?? currentUser
    : null;

  return (
    <div className="leaderboard-page">
      <div className="lb-inner">

        <div className="lb-header">
          <h1>Leaderboard</h1>
          <p>Ranked by best single-game score</p>
        </div>

        {loading ? (
          <div className="lb-table">
            {Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        ) : ranked.length === 0 ? (
          <div className="lb-empty">No players yet — be the first!</div>
        ) : (
          <>
            {top3.length > 0 && (
              <div className="podium">
                <PodiumSlot player={top3[1]} rank={2} isCurrentUser={currentUser?.id === top3[1]?.id} level={getLevelForScore(levels, top3[1]?.total_score || 0)} />
                <PodiumSlot player={top3[0]} rank={1} isCurrentUser={currentUser?.id === top3[0]?.id} level={getLevelForScore(levels, top3[0]?.total_score || 0)} />
                <PodiumSlot player={top3[2]} rank={3} isCurrentUser={currentUser?.id === top3[2]?.id} level={getLevelForScore(levels, top3[2]?.total_score || 0)} />
              </div>
            )}

            {(rest.length > 0 || currentUserEntry) && (
              <div className="lb-table">
                {rest.map((player, i) => {
                  const rank = i + 4;
                  const isCurrentUser = currentUser?.id === player.id;
                  // Cap stagger so very long lists don't take forever.
                  const rowDelay = 600 + Math.min(i, 12) * 60;
                  return (
                    <div
                      key={player.id}
                      className={['lb-row', isCurrentUser ? 'lb-row-you' : ''].filter(Boolean).join(' ')}
                    >
                      <span className="lb-rank">#{rank}</span>
                      <div className="lb-avatar">
                        {player.avatar
                          ? <img src={avatarSrc(player.avatar)} alt="" />
                          : player.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="lb-info">
                        <span className="lb-name">
                          {player.name}
                          {isCurrentUser && <span className="you-tag">You</span>}
                        </span>
                        <span className="lb-meta-line">
                          {player.games_played} games · avg <AnimatedScore value={player.average_score || 0} delay={rowDelay} duration={1100} />
                        </span>
                      </div>
                      <button
                        type="button"
                        className="lb-level-btn rd-trigger"
                        onClick={() => openRanks(player.total_score || 0)}
                        aria-label={`View rank tiers (player level)`}
                      >
                        <LevelPill level={getLevelForScore(levels, player.total_score || 0)} />
                      </button>
                      <span className="lb-score">
                        <AnimatedScore value={player.best_score || 0} delay={rowDelay} duration={1200} />
                      </span>
                    </div>
                  );
                })}
                {currentUserEntry && (
                  <div className="lb-row lb-row-you lb-row-unranked">
                    <span className="lb-rank">—</span>
                    <div className="lb-avatar">
                      {currentUserEntry.avatar
                        ? <img src={avatarSrc(currentUserEntry.avatar)} alt="" />
                        : currentUserEntry.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="lb-info">
                      <span className="lb-name">
                        {currentUserEntry.name}
                        <span className="you-tag">You</span>
                      </span>
                      <span className="lb-meta-line">
                        Play a round to join the rankings
                      </span>
                    </div>
                    <button
                      type="button"
                      className="lb-level-btn rd-trigger"
                      onClick={() => openRanks(currentUserEntry.total_score || 0)}
                      aria-label="View rank tiers"
                    >
                      <LevelPill level={getLevelForScore(levels, currentUserEntry.total_score || 0)} />
                    </button>
                    <span className="lb-score">—</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </div>

      <RanksDialog
        open={ranksOpen}
        onClose={() => setRanksOpen(false)}
        currentScore={dialogScore}
      />
    </div>
  );
};

export default Leaderboard;
